// app/javascript/controllers/overlay_controller.js
import { Controller } from "@hotwired/stimulus"

/**
 * Overlay Controller - The proper abstraction for all overlay management
 * 
 * Handles full-screen overlays, modals, sidebars, and any UI element that needs:
 * - Show/hide with CSS class management
 * - Click-away detection 
 * - Escape key handling
 * - Body scroll prevention
 * - Proper event lifecycle management
 * 
 * EXISTING CONTROLLERS THAT DUPLICATE THIS FUNCTIONALITY:
 * - remote_modal_controller.js (direct replacement candidate)
 * - mobile_filter_controller.js (will be deleted after this implementation)
 * - navigation_controller.js (partial overlap - panel management could be migrated)
 * 
 * USAGE EXAMPLES:
 * - Mobile filter overlays (current use case)
 * - Modal dialogs (replace remote_modal_controller.js)
 * - Mobile navigation panels 
 * - Dropdown overlays
 * - Sidebar toggles
 * - Any full-screen or positioned overlay
 * 
 * INTEGRATION WITH OTHER CONTROLLERS:
 * - Use with focus-trap controller for accessibility
 * - Does NOT interfere with form submission, filtering, or pagination
 * - Purely handles overlay presentation layer
 */
export default class extends Controller {
  static targets = ["trigger", "overlay", "content"]
  static values = {
    hiddenClass: { type: String, default: "hidden" },
    bodyScrollClass: { type: String, default: "overflow-hidden" }
  }

  connect() {
    this._clickAway = this._clickAway.bind(this)
    this._handleEscape = this._handleEscape.bind(this)
  }

  disconnect() {
    this._cleanup()
    // Ensure body scroll is restored if controller is removed while overlay is open
    document.body.classList.remove(this.bodyScrollClassValue)
  }

  /**
   * Show the overlay
   * - Removes hidden class from overlay
   * - Prevents body scrolling
   * - Sets up click-away and escape key listeners
   */
  show(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove(this.hiddenClassValue)
      document.body.classList.add(this.bodyScrollClassValue)
      
      // Set up event listeners when overlay is shown
      document.addEventListener("click", this._clickAway)
      document.addEventListener("keydown", this._handleEscape)
    }
  }

  /**
   * Hide the overlay
   * - Adds hidden class to overlay  
   * - Restores body scrolling
   * - Removes event listeners
   */
  hide(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add(this.hiddenClassValue)
      document.body.classList.remove(this.bodyScrollClassValue)
      
      this._cleanup()
    }
  }

  /**
   * Toggle overlay visibility
   */
  toggle(event) {
    if (this.hasOverlayTarget) {
      if (this.overlayTarget.classList.contains(this.hiddenClassValue)) {
        this.show(event)
      } else {
        this.hide(event)
      }
    }
  }

  /**
   * Handle clicks outside the overlay content area
   * Closes overlay when clicking on the backdrop
   */
  _clickAway(event) {
    // If we have a content target, check if click is outside it
    if (this.hasContentTarget) {
      if (!this.contentTarget.contains(event.target)) {
        this.hide()
      }
    } else if (this.hasOverlayTarget) {
      // Fallback: check if click is on the overlay background itself
      if (event.target === this.overlayTarget) {
        this.hide()
      }
    }
  }

  /**
   * Handle escape key to close overlay
   */
  _handleEscape(event) {
    if (event.key === "Escape") {
      this.hide()
    }
  }

  /**
   * Clean up event listeners
   */
  _cleanup() {
    document.removeEventListener("click", this._clickAway)
    document.removeEventListener("keydown", this._handleEscape)
  }
}